import {IAppConstants} from '../app.constants';
import {IHttpResponse, IPromise} from 'angular';
import {IClassiDocumentali, IUser} from '../commons/interfaces/user/user';
import {IInvoices} from '../commons/interfaces/user/invoices';
import {IStorageUsage} from '../commons/interfaces/user/storage';
import {IFatturaAttivaItem} from '../commons/interfaces/invoices/attive';
import {ITimestamps} from '../commons/interfaces/timestamps';
import {IListini} from '../commons/interfaces/listini';
import {IRivenditore} from '../commons/interfaces/IRivenditore';
import {INotification} from '../commons/interfaces/notifications/INotification';
import {IResultResponse} from '../commons/interfaces/IResultResponse';
import {INotificationFilter} from '../sections/home/notifiche/notifiche';
import {IActivationCode} from '../commons/interfaces/IActivationCode';
import {IInsertActivationCodeRequest} from '../commons/interfaces/insertActivationCodeRequest';
import {IApiSecretKey} from '../commons/interfaces/IApiSecretKey';
import {IStorageUsageClassDocStatistics} from '../commons/interfaces/storageUsageClassDocStatistics';
import {IStorageArchiviaUser} from '../commons/interfaces/IStorageUsageUser';
import {IDocToConserveStatistics} from '../commons/interfaces/IDocToConserveStatistics';

export class ArchiSmallServices {
  static $inject: string[] = ['$http', 'configuration', '$q', 'store'];

  private baseUrl: string;
  private user: any;

  constructor(
    private $http: ng.IHttpService,
    private configuration: IAppConstants,
    private $q: ng.IQService,
    private store: ng.a0.storage.IStoreService
  ) {
    this.baseUrl = configuration.restServer;
    this.user = store.get('user');
  }

  version(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/users/version`);
  }

  validateUsername(username: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/validate/username/${username}`);
  }

  validateEmail(email: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/validate/email/${email}`);
  }

  validatePIVA(piva: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/validate/piva/${piva}`);
  }

  getTypeaheadValues(option: string, filter?: string): IPromise<IHttpResponse<string[]>> {
    let _url = `${this.baseUrl}/typeahead/${option}`;
    if (filter != null) {
      _url += `?${filter}`;
    }
    return this.$http.get(_url);
  }

  getClassiDocumentali(): IPromise<IHttpResponse<IClassiDocumentali>> {
    return this.$http.get(`${this.baseUrl}/users/classiDocumentali`);
  }

  registerUser(user: any): IPromise<IHttpResponse<any>> {
    return this.$http.post(`${this.baseUrl}/users/register`, user, {responseType: 'application/json'});
  }

  renewSubscription(): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/request-renew-subscription`, undefined);
  }

  userLogin(credentials: any): IPromise<IHttpResponse<any>> {
    return this.$http.post(`${this.baseUrl}/users/login`, credentials, {responseType: 'application/json'});
  }

  getUser(email: string): IPromise<IHttpResponse<IUser>> {
    return this.$http.get(`${this.baseUrl}/users/${email}`);
  }

  getUsers(): IPromise<IHttpResponse<IUser[]>> {
    return this.$http.get(`${this.baseUrl}/admin/users`);
  }

  getUserByPiva(piva: string): IPromise<IHttpResponse<IUser>> {
    return this.$http.get(`${this.baseUrl}/admin/userByPiva/${piva}`);
  }

  getStorageUsage(): IPromise<IHttpResponse<IStorageUsage>> {
    return this.$http.get(`${this.baseUrl}/users/storage-usage`);
  }

  getStorageUsageFe(): IPromise<IHttpResponse<IStorageUsage>> {
    return this.$http.get(`${this.baseUrl}/users/storage-usage-fe`);
  }

  getStorageArchive(email: string, years: number[]): IPromise<IHttpResponse<IResultResponse<IStorageArchiviaUser[]>>> {
    return this.$http.get(`${this.baseUrl}/users/storage-usage-archive/${email}`, {params: {years}});
  }

  getStorageUsageCeYears(email: string, years: number[] = []): IPromise<IHttpResponse<IStorageUsage[]>> {
    return this.$http.get(`${this.baseUrl}/users/storage-usage/${email}`, {params: {years}});
  }

  getStorageUsageClassDocEmailYears(email: string, years: number[] = []): IPromise<IHttpResponse<IStorageUsageClassDocStatistics[]>> {
    return this.$http.get(`${this.baseUrl}/users/storage-usage-class-doc/${email}`, {params: {years}});
  }

  getInvoices(email: string, years: number[]): IPromise<IHttpResponse<IInvoices[]>> {
    return this.$http.post(`${this.baseUrl}/users/invoicesUsage/${email}`, years);
  }

  getConnectionCount(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/users/connection-count`);
  }

  updateUser(user: IUser): IPromise<IHttpResponse<IUser>> {
    return this.$http.put(`${this.baseUrl}/users/update`, user);
  }

  updateOtherUser(userId: string, user: IUser, differences: string): IPromise<IHttpResponse<IUser>> {
    user.last_differences = differences;
    return this.$http.put(`${this.baseUrl}/users/update/${userId}`, user);
  }

  updateNotificiationsDisabled(user: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/update-notifications-disabled`, user);
  }

  updatePassword(password: any): IPromise<IHttpResponse<any>> { // object with oldPass + newPass + email
    return this.$http.put(`${this.baseUrl}/users/change-password`, password);
  }

  recoverPassword(email: any): IPromise<IHttpResponse<any>> { // object with oldPass + newPass + email
    return this.$http.post(`${this.baseUrl}/users/recover-password`, email);
  }

  changePassword(data: any): IPromise<IHttpResponse<any>> { // object with oldPass + newPass + email
    return this.$http.put(`${this.baseUrl}/users/change-password`, data);
  }

  audit(settings: any): IPromise<IHttpResponse<any>> {
    return this.$http.post(`${this.baseUrl}/users/audit`, settings);
  }

  rabbitMQConfig(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/users/rabbitMQ-config`);
  }

  getGoogleAPIToken(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/users/generate-token-google`);
  }

  validateGoogleAPIToken(_token: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${_token}`);
  }

  updatePersonalFiles(files: any): IPromise<IHttpResponse<any>> { // object with oldPass + newPass + email
    return this.$http.put(`${this.baseUrl}/users/update-personal-files`, files);
  }

  getConservazioneDocuments(filter: any, sortField: string, sortDirection: number): IPromise<IHttpResponse<any>> {
    let _filter: string = encodeURIComponent(JSON.stringify(filter));
    return this.$http.get(`${this.baseUrl}/conservazione/get-documents?filter=${_filter}&sortField=${sortField}&sortDirection=${sortDirection}`);
  }

  getFatturazioneDocuments(filter: any, sortField: string, sortDirection: number): IPromise<IHttpResponse<IResultResponse<IFatturaAttivaItem>>> {
    let _filter: string = encodeURIComponent(JSON.stringify(filter));
    return this.$http.get(`${this.baseUrl}/fatturapa/get-documents?filter=${_filter}&sortField=${sortField}&sortDirection=${sortDirection}`);
  }

  getFatturazioneDocumentsCsv(filter: any, sortField: string, sortDirection: number): IPromise<IHttpResponse<any>> {
    let _filter: string = encodeURIComponent(JSON.stringify(filter));
    return this.$http.get(`${this.baseUrl}/fatturapa/get-documents/csv?filter=${_filter}&sortField=${sortField}&sortDirection=${sortDirection}`);
  }

  getArchidriveDocuments(filter: any): IPromise<IHttpResponse<any>> {
    let _filter: string = encodeURIComponent(JSON.stringify(filter));
    return this.$http.get(`${this.baseUrl}/archidrive/get-documents?filter=${_filter}`);
  }

  listArchidriveDirectory(filter: any): IPromise<IHttpResponse<any>> {
    let _filter: string = encodeURIComponent(JSON.stringify(filter));
    return this.$http.get(`${this.baseUrl}/archidrive/list-directory?filter=${_filter}`);
  }

  createArchidriveDirectory(settings: string): IPromise<IHttpResponse<any>> {
    // todo: verificare il secondo parametro
    return this.$http.post(`${this.baseUrl}/archidrive/create-directory?${settings}`, null);
  }

  getAnagraficaItems(filter: any): IPromise<IHttpResponse<any>> {
    let _filter: string = encodeURIComponent(JSON.stringify(filter));
    return this.$http.get(`${this.baseUrl}/anagrafica/get?filter=${_filter}`);
  }

  updateAnagrafica(anagrafica: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/anagrafica/update`, anagrafica);
  }

  insertAnagrafica(anagrafica: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/anagrafica/set`, anagrafica);
  }

  deleteAnagrafica(ids: any): IPromise<IHttpResponse<any>> {
    let _ids: string = encodeURIComponent(ids);
    return this.$http.delete(`${this.baseUrl}/anagrafica/delete/${_ids}`);
  }

  validateAnagraficaPIVA(piva: string, id: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/anagrafica/validate/piva/${piva}/id/${id}`);
  }

  validateAnagraficaCF(cf: string, id: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/anagrafica/validate/fiscal-code/${cf}/id/${id}`);
  }

  moveArchidrive(settings: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/archidrive/move`, settings);
  }

  generateFatturaPA(settings: any): IPromise<IHttpResponse<any>> {
    return this.$http.post(`${this.baseUrl}/users/generate-fattura-pa`, settings, {responseType: 'application/json'});
  }

  getFatturaPA(path: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/fatturapa/fattura-pa-xml?path=${path}`);
  }

  fattureAttiveTemplate(data: any): IPromise<IHttpResponse<any>> {
    return this.$http.post(`${this.baseUrl}/conservazione/fatture-attive-template`, data, {responseType: 'application/json'});
  }

  deleteArchidriveFile(paths: any): IPromise<IHttpResponse<any>> {
    let _paths = encodeURIComponent(JSON.stringify(paths));
    return this.$http.delete(`${this.baseUrl}/archidrive/${_paths}`);
  }

  renameArchidriveFile(settings: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/archidrive/rename`, settings);
  }

  deleteFattureAttive(id: any): IPromise<IHttpResponse<any>> {
    let _id: string = encodeURIComponent(id);
    return this.$http.delete(`${this.baseUrl}/conservazione/fatture-attive/${_id}`);
  }

  updateConservazioneDocument(document: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/conservazione/update`, document);
  }

  deleteConservazioneDocument(documents: string[]): IPromise<IHttpResponse<any>> {
    return this.$http.delete(`${this.baseUrl}/conservazione/delete/${documents.join(',')}`);
  }

  updateConservazioneDIPStatus(document: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/conservazione/update-dip-status`, document);
  }

  updatePECDocument(document: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/conservazione/update-pec`, document);
  }

  validateActivation(token: string): IPromise<IHttpResponse<any>> {
    // todo: verificare il secondo parametro
    return this.$http.put(`${this.baseUrl}/users/activate?token=${token}`, null);
  }

  getSubscriptions(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/payment/offers`);
  }

  paymentTransaction(transaction: any): IPromise<IHttpResponse<any>> {
    return this.$http.post(`${this.baseUrl}/payment/transaction`, transaction, {responseType: 'application/json'});
  }

  paymentSubscription(subscription: any): IPromise<IHttpResponse<any>> {
    return this.$http.post(`${this.baseUrl}/payment/subscription`, subscription, {responseType: 'application/json'});
  }

  paymentChecksum(subscriptionId: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/payment/paypal-checksum?subscriptionId=${subscriptionId}`);
  }

  validateTransaction(transactionId: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/payment/validate-transaction?transactionId=${transactionId}`);
  }

  notifications(userEmail: string, filter: INotificationFilter = {
    seen: false,
    pageSizes: undefined,
    pageNumber: 1,
    pageSize: 20
  }): IPromise<IHttpResponse<INotification[]>> {
    return this.$http.get(`${this.baseUrl}/notifications/${userEmail}`, {
      params: filter ? {
        seen: filter.seen,
        limit: filter.pageSize,
        skip: (filter.pageNumber - 1) * filter.pageSize
      } : undefined
    });
  }

  notificationConsume(idNotificationArr: string[]): IPromise<IHttpResponse<INotification>> {
    return this.$http.put(`${this.baseUrl}/notifications/consume`, idNotificationArr);
  }

  notificationUpdate(notifications: INotification[]): IPromise<IHttpResponse<INotification>> {
    return this.$http.put(`${this.baseUrl}/notifications/update`, notifications);
  }

  notificationConsumeAll(): IPromise<IHttpResponse<{ Result: number }>> {
    return this.$http.put(`${this.baseUrl}/notifications/consumeAll`, null);
  }

  validateActivationCode(activationCode: string, PIVA?: string, fiscalCode?: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/activation-code/validate/`, {
      params: {
        activationCode: activationCode,
        PIVA: PIVA,
        fiscalCode: fiscalCode
      }
    });
  }

  insertActivationCode(data: IInsertActivationCodeRequest): IPromise<IHttpResponse<IActivationCode>> {
    return this.$http.put(`${this.baseUrl}/activation-code/insert/`, data);
  }

  getActivationCode(): IPromise<IHttpResponse<IActivationCode>> {
    return this.$http.get(`${this.baseUrl}/activation-code/get/`);
  }

  enableConservazione(user: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/enable-conservazione`, user);
  }

  enableFatturazione(user: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/enable-fattura-pa`, user);
  }

  fatturaPaCessionary(name: string, city: string, code: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/fatturapa/cessionary?name=${name}&city=${city}&code=${code}`);
  }

  unsubscribe(user: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/unsubscribe`, user);
  }

  getRabbitMQConfig(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/users/rabbitMQ-config`);
  }

  resendActivationMail(email: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/resend-activation-mail`, email);
  }

  getUploadFilenames(files: any): IPromise<IHttpResponse<any>> {
    return this.$http.post(`${this.baseUrl}/archidrive/get-upload-filename`, files);
  }

  getRegistrationJWT(activationCode: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/activation-code/get-registration-token/?activationCode=${activationCode}`);
  }

  getConservazioneSettings(userSettings: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/users/get-settings?key=${userSettings}`);
  }

  setConservazioneSettings(userSettings: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/set-settings`, userSettings);
  }

  deleteConservazioneSettings(userSettings: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/delete-settings`, userSettings);
  }

  getAnagraficaTypeaheadValues(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/anagrafica/typeahead/`);
  }

  getUserFromProvision(activationCode: string): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/nsrequest/user/${activationCode}`);
  }

  checkSubscription(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/users/check-subscription/`);
  }

  getUnsubscriptionArchives(type: any): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/unsubscription/archives/get-list/${type}`);
  }

  updateUnsubscriptionArchiveDownloadedStatus(archive: any): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/unsubscription/update-downloaded-status`, archive);
  }

  getFatturaAttachment(path: any): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/fatturapa/attachment?path=${path}`);
  }

  getRdvFile(fileId: any): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/conservazione-tdoc/get-rdv?fileId=${fileId}`);
  }

  uploadFile(file: any, bucket: string, name: string): IPromise<IHttpResponse<any>> {
    const baseUrl = 'https://storage.googleapis.com/upload/storage/v1/b/';
    const accessToken = localStorage.getItem('_GoogleAccessToken');
    let options = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': file.type || 'application/octet-stream'
      },
      transformRequest: []
    };
    return this.$http.post(`${baseUrl}${bucket}/o?uploadType=media&name=${name}`, file, options);
  }

  validateDigitalSignature(bucket: any, fileName: any) {
    return this.$http.get(this.baseUrl + '/conservazione/digital-signature/' + bucket + '/' + fileName);
  }

  confirmServiceLevelAgreement(): IPromise<IHttpResponse<any>> {
    return this.$http.put(`${this.baseUrl}/users/confirm-service-level-agreement`, undefined);
  }

  getNextSendToConservazioneDate(): IPromise<IHttpResponse<any>> {
    return this.$http.get(`${this.baseUrl}/conservazione/get-next-send-to-conservazione-date`);
  }

  getRemainingTimestamps(): IPromise<IHttpResponse<ITimestamps[]>> {
    return this.$http.get(`${this.baseUrl}/admin/timestamps`);
  }

  getLastTimestamps(): IPromise<IHttpResponse<ITimestamps>> {
    return this.$http.get(`${this.baseUrl}/admin/timestamps/last`);
  }

  createListino(listino: IListini): IPromise<IHttpResponse<void>> {
    return this.$http.post(`${this.baseUrl}/admin/listini`, listino);
  }

  getListino(id: string): IPromise<IHttpResponse<IListini>> {
    return this.$http.get(`${this.baseUrl}/admin/listini/${id}`);
  }

  getListinoCsv(id: string): IPromise<IHttpResponse<IListini>> {
    return this.$http.get(`${this.baseUrl}/admin/listino/csv/${id}`);
  }

  getListini(): IPromise<IHttpResponse<IListini[]>> {
    return this.$http.get(`${this.baseUrl}/admin/listini`);
  }

  deleteListino(id: string): IPromise<IHttpResponse<boolean>> {
    return this.$http.delete(`${this.baseUrl}/admin/listini/${id}`);
  }

  updateListino(listino: IListini): IPromise<IHttpResponse<IListini>> {
    return this.$http.put(`${this.baseUrl}/admin/listini`, listino);
  }

  createRivenditore(rivenditore: IRivenditore): IPromise<IHttpResponse<void>> {
    return this.$http.post(`${this.baseUrl}/admin/rivenditori`, rivenditore);
  }

  getRivenditore(id: string): IPromise<IHttpResponse<IRivenditore>> {
    return this.$http.get(`${this.baseUrl}/admin/rivenditori/${id}`);
  }

  getRivenditori(): IPromise<IHttpResponse<IRivenditore[]>> {
    return this.$http.get(`${this.baseUrl}/admin/rivenditori`);
  }

  getRivenditoriCsv(): IPromise<IHttpResponse<IRivenditore[]>> {
    return this.$http.get(`${this.baseUrl}/admin/rivenditori/csv`);
  }

  deleteRivenditore(id: string): IPromise<IHttpResponse<boolean>> {
    return this.$http.delete(`${this.baseUrl}/admin/rivenditori/${id}`);
  }

  updateRivenditore(rivenditore: IRivenditore): IPromise<IHttpResponse<IRivenditore>> {
    return this.$http.put(`${this.baseUrl}/admin/rivenditori`, rivenditore);
  }

  registerRivenditore(rivenditore: IRivenditore): IPromise<IHttpResponse<void>> {
    return this.$http.put(`${this.baseUrl}/admin/register-rivenditori`, rivenditore);
  }

  uploadFatturePassiveAdmin(user: IUser, dati: {
    xml: { file: string, name: string },
    metadati: { file: string, name: string }
  }[]): IPromise<IHttpResponse<{ message: string, name: string, idSdi: number, error: boolean }[]>> {
    return this.$http.post(`${this.baseUrl}/admin/fatture-passive/${user.accountData.email}`, dati);
  }

  insertApiSecretKey(dati: IApiSecretKey): IPromise<IHttpResponse<IApiSecretKey>> {
    return this.$http.post(`${this.baseUrl}/admin/api-secret-key`, dati);

  }

  getDocToConserveStatisticsItems(): IPromise<IHttpResponse<IDocToConserveStatistics[]>> {
    return this.$http.get(`${this.baseUrl}/admin/documents-to-conserve`);
  }

  getDocToConserveStatisticsItemsUser(userId: String): IPromise<IHttpResponse<IDocToConserveStatistics[]>> {
    return this.$http.get(`${this.baseUrl}/users/documents-to-conserve/${userId}`);
  }

  uploadFatturaPassiva(user: IUser, dati: {
    xml: { file: string, name: string },
    metadati: { file: string, name: string }
  }): IPromise<IHttpResponse<number>> {
    return this.$http.post(`${this.baseUrl}/fattura-elettronica/passiva/import-multi`, dati);
  }

  uploadFatturePassive(user: IUser, dati: {
    xml: { file: string, name: string },
    metadati: { file: string, name: string }
  }[]): IPromise<IHttpResponse<{ message: string, name: string, idSdi: number, error: boolean }[]>> {
    return this.$http.post(`${this.baseUrl}/fattura-elettronica/passiva/import-multi`, dati);
  }

  downloadFatturaXml(id: string, name: string) {
    this.$http.get(`${this.baseUrl}/fatturapa/download?id=${id}`, {responseType: 'text/xml'}).then((res) => {
      console.log(res);

      let link = document.createElement('a');
      link.download = name;
      link.href = `data:application/octet-stream;base64,${res['fattura']}`;
      link.click();
    });
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/services/archismall.service.ts